import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, RefreshCw, TrendingUp, TrendingDown } from "lucide-react";
import { formatBRL } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_VEICULO = {
  disponivel: { label: "Disponível", cls: "bg-emerald-500/15 text-emerald-400" },
  reservado: { label: "Reservado", cls: "bg-amber-500/15 text-amber-400" },
  vendido: { label: "Vendido", cls: "bg-slate-500/15 text-slate-400" },
};

export const CustosTab = ({ onUnauthorized }) => {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/custos`, { headers });
      setItems(res.data);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setItems([]); toast.error("Erro ao carregar custos."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const Row = ({ label, value, strong, tone }) => (
    <div className="flex justify-between text-sm py-1.5">
      <span className="text-slate-400">{label}</span>
      <span className={`${strong ? "font-bold" : "font-medium"} ${tone || "text-white"}`}>{value}</span>
    </div>
  );

  return (
    <div data-testid="custos-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Centro de custos por veículo</h1>
          <p className="text-sm text-slate-400 mt-1">Compra + gastos lançados no Financeiro (categoria Veículo) = custo total do carro.</p>
        </div>
        <button onClick={load} data-testid="custos-refresh" className="inline-flex items-center gap-2 glass hover:border-emerald-500/50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      {items === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-[#131620] animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <p className="text-slate-400 text-sm glass rounded-2xl p-8" data-testid="custos-empty">Nenhum veículo cadastrado.</p>
      ) : (
        <div className="space-y-3" data-testid="custos-list">
          {items.map((it) => {
            const v = it.veiculo;
            const st = STATUS_VEICULO[v.status || "disponivel"];
            const lucroPrev = it.lucro_previsto;
            return (
              <button
                key={v.id}
                onClick={() => setSelected(it)}
                className="w-full text-left glass rounded-2xl p-4 sm:p-5 hover:border-emerald-500/40 transition-colors"
                data-testid={`custo-vehicle-${v.id}`}
              >
                <div className="flex items-center gap-4">
                  <img src={v.imagem} alt={v.nome} className="w-24 h-16 object-cover rounded-xl shrink-0 hidden sm:block" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                      {v.nome}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                    </p>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-1 text-xs">
                      <span className="text-slate-400">Compra: <span className="text-white font-medium">{formatBRL(v.preco_custo || 0)}</span></span>
                      <span className="text-slate-400">Gastos: <span className="text-white font-medium">{formatBRL(it.total_gastos)}</span> ({it.gastos.length})</span>
                      {it.ganhos_locacao > 0 && (
                        <span className="text-slate-400">Locação: <span className="text-emerald-400 font-medium">{formatBRL(it.ganhos_locacao)}</span> ({it.locacoes}x)</span>
                      )}
                      <span className="text-slate-400">Custo líquido: <span className="text-white font-semibold">{formatBRL(it.custo_liquido)}</span></span>
                      <span className="text-slate-400">Anunciado: <span className="text-emerald-400 font-medium">{formatBRL(v.preco)}</span></span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {it.venda ? (
                      <>
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">Lucro líquido</p>
                        <p className={`font-display font-bold text-lg flex items-center gap-1 justify-end ${it.venda.lucro_liquido >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {it.venda.lucro_liquido >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          {formatBRL(it.venda.lucro_liquido)}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] uppercase tracking-wide text-slate-500">Lucro previsto</p>
                        <p className={`font-display font-bold text-lg ${lucroPrev >= 0 ? "text-emerald-400" : "text-red-400"}`}>{formatBRL(lucroPrev)}</p>
                      </>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            data-testid="custo-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-7 w-full max-w-lg max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-display text-xl font-bold text-white">{selected.veiculo.nome}</h2>
                  <p className="text-xs text-slate-400">{selected.veiculo.marca} · {selected.veiculo.ano}</p>
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white" data-testid="custo-modal-close"><X className="w-5 h-5" /></button>
              </div>

              <Row label="Compra (valor inicial)" value={formatBRL(selected.veiculo.preco_custo || 0)} />
              <div className="my-2 border-t border-slate-800/60" />
              {selected.gastos.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">Nenhum gasto lançado. Lance no Financeiro com a categoria "Veículo".</p>
              ) : (
                selected.gastos.map((g) => (
                  <Row key={g.id} label={`+ ${g.descricao}`} value={formatBRL(g.valor)} />
                ))
              )}
              <div className="my-2 border-t border-slate-800/60" />
              <Row label="Custo total (compra + gastos)" value={formatBRL(selected.custo_total)} />
              {selected.ganhos_locacao > 0 && (
                <Row label={`− Ganhos com locação (${selected.locacoes}x)`} value={formatBRL(selected.ganhos_locacao)} tone="text-emerald-400" />
              )}
              <Row label="CUSTO LÍQUIDO ATÉ HOJE" value={formatBRL(selected.custo_liquido)} strong />
              <Row label="Preço anunciado" value={formatBRL(selected.veiculo.preco)} />
              <Row
                label="Lucro previsto"
                value={`${formatBRL(selected.lucro_previsto)}${selected.custo_total > 0 ? ` (${((selected.lucro_previsto / selected.custo_total) * 100).toFixed(1)}%)` : ""}`}
                strong tone={selected.lucro_previsto >= 0 ? "text-emerald-400" : "text-red-400"}
              />

              {selected.venda && (
                <div className="mt-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 p-5">
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-400 mb-2">Venda realizada</p>
                  <Row label="Vendido por" value={formatBRL(selected.venda.valor)} />
                  <Row label="− Custo total" value={formatBRL(selected.venda.custo_total || 0)} />
                  {(selected.venda.ganhos_locacao || 0) > 0 && <Row label="+ Ganhos com locação" value={formatBRL(selected.venda.ganhos_locacao)} tone="text-emerald-400" />}
                  {selected.venda.comissao > 0 && <Row label="− Comissão" value={formatBRL(selected.venda.comissao)} />}
                  {selected.venda.imposto_valor > 0 && <Row label={`− Imposto (${selected.venda.imposto_pct}%)`} value={formatBRL(selected.venda.imposto_valor)} />}
                  <div className="my-2 border-t border-emerald-500/30" />
                  <Row label="LUCRO LÍQUIDO REALIZADO" value={formatBRL(selected.venda.lucro_liquido)} strong tone={selected.venda.lucro_liquido >= 0 ? "text-emerald-400" : "text-red-400"} />
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
